import { useEffect, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { ArrowRight, RefreshCw, Radar, Zap, Bell, Check, HelpCircle } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { OnboardingDialog } from '@/components/OnboardingDialog';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { ScoreDial } from '@/components/ScoreDial';
import { Sparkline } from '@/components/Sparkline';
import { SignalBadge } from '@/components/SignalBadge';
import { CyclePhaseBar } from '@/components/CyclePhaseBar';
import { ChangeCard } from '@/components/ChangeCard';
import { IndicatorDetailDialog } from '@/components/IndicatorDetailDialog';
import { FeedHealth, DataStatus } from '@/components/DataStatus';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useLiquidityData } from '@/hooks/useLiquidityData';
import { useSubscription, PRICING } from '@/hooks/useSubscription';
import { PHASE_META } from '@/lib/scoring';
import { INDICATORS_BY_ID } from '@/lib/indicators';
import { formatValue, signed } from '@/lib/format';
import type { IndicatorSnapshot } from '@/lib/types';
import { cn } from '@/lib/utils';

/** Plain-language interpretation of a domain score, split by domain. */
function interpretation(domain: 'liquidity' | 'bch', score: number): string {
  if (domain === 'liquidity') {
    if (score >= 65) return 'Liquidity conditions are broadly supportive.';
    if (score >= 50) return 'Liquidity conditions are modestly supportive.';
    if (score >= 40) return 'Liquidity conditions are currently mixed.';
    if (score >= 25) return 'Liquidity conditions are tightening.';
    return 'Liquidity conditions are restrictive.';
  }
  if (score >= 65) return "BCH's environment is strengthening versus BTC.";
  if (score >= 50) return "BCH's environment is gradually improving.";
  if (score >= 40) return 'BCH is developing but not yet confirmed.';
  return 'BCH remains weak relative to BTC.';
}

const Index = () => {
  useSeoMeta({
    title: 'BCH Radar — Know what\'s changing',
    description:
      'A 24/7 macro-liquidity intelligence dashboard for Bitcoin Cash. Transparent Global Liquidity & BCH Setup scores, change detection, daily briefs, and alerts.',
  });

  const { data, isLoading, isFetching, refetch } = useLiquidityData();
  const [selected, setSelected] = useState<IndicatorSnapshot | null>(null);

  // First-visit onboarding walkthrough. Shown once, re-openable anytime.
  const [onboarded, setOnboarded] = useLocalStorage('bchliq:onboarded', false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    if (!onboarded) {
      const t = setTimeout(() => setShowOnboarding(true), 600);
      return () => clearTimeout(t);
    }
  }, [onboarded]);
  const handleOnboardingChange = (v: boolean) => {
    setShowOnboarding(v);
    if (!v) setOnboarded(true);
  };

  return (
    <AppLayout>
      <OnboardingDialog open={showOnboarding} onOpenChange={handleOnboardingChange} />
      {isLoading || !data ? (
        <DashboardSkeleton />
      ) : (
        <div className="space-y-6">
          {/* Header row */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Macro Radar
              </h1>
              <p className="mt-1.5">
                <FeedHealth
                  available={data.availableCount}
                  total={data.totalIndicators}
                  lastUpdated={data.lastUpdated}
                />
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOnboarding(true)}
                className="gap-2 text-muted-foreground"
              >
                <HelpCircle className="h-4 w-4" />
                <span className="hidden sm:inline">How to read this</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
                className="gap-2"
              >
                <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>

          {/* Score cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            <ScoreCard
              domain="liquidity"
              title="Global Liquidity"
              subtitle="Are liquidity conditions improving?"
              score={data.snapshot.liquidity.score}
              label={data.snapshot.liquidity.label}
              state={data.snapshot.liquidity.state}
              trend={data.trend30d.liquidity}
              series={data.history.map((h) => h.liquidity)}
              to="/indicators?tab=liquidity"
              colorClass="text-primary"
            />
            <ScoreCard
              domain="bch"
              title="BCH Setup"
              subtitle="Is BCH's environment improving?"
              score={data.snapshot.bch.score}
              label={data.snapshot.bch.label}
              state={data.snapshot.bch.state}
              trend={data.trend30d.bch}
              series={data.history.map((h) => h.bch)}
              to="/indicators?tab=bch"
              colorClass="text-chart-2"
            />
          </div>

          {/* Cycle */}
          <Card>
            <CardContent className="space-y-4 p-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Current Phase
                </p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-base font-bold sm:text-lg">
                  <span>{PHASE_META[data.snapshot.phase].label.toUpperCase()}</span>
                  {data.trendReliable && (
                    <>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {(PHASE_META[data.snapshot.phase].order < 4
                          ? PHASE_META[
                              (['tightening', 'bottoming', 'easing', 'expansion', 'mania'] as const)[
                                PHASE_META[data.snapshot.phase].order + 1
                              ]
                            ].label
                          : 'PEAK'
                        ).toUpperCase()}
                      </span>
                    </>
                  )}
                </p>
              </div>
              <CyclePhaseBar phase={data.snapshot.phase} />
              <p className="text-sm text-muted-foreground">
                {PHASE_META[data.snapshot.phase].description}
              </p>
              {!data.trendReliable && (
                <p className="rounded-md border border-yellow-500/30 bg-yellow-500/5 p-2.5 text-xs text-yellow-200/90">
                  Based on current conditions. The 30-day trend needed to
                  confirm cycle direction is still building — this phase
                  reflects where liquidity sits today, not a confirmed move.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Radar Activity / What changed */}
          <section>
            <SectionHeading
              title="Radar Activity"
              subtitle="The most important recent moves"
            />
            <div className="mt-3 space-y-2">
              {data.changes.length === 0 ? (
                <RadarBaseline
                  indicators={topDrivers(
                    data.snapshot.liquidity.indicators.concat(
                      data.snapshot.bch.indicators,
                    ),
                  )}
                  onSelect={setSelected}
                />
              ) : (
                data.changes
                  .slice(0, 3)
                  .map((c) => <ChangeCard key={c.id} change={c} />)
              )}
            </div>
          </section>

          {/* What to watch */}
          <section>
            <SectionHeading
              title="What to Watch"
              subtitle="Indicators driving the signal right now"
            />
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {topDrivers(data.snapshot.liquidity.indicators.concat(
                data.snapshot.bch.indicators,
              )).map((snap) => (
                <WatchTile
                  key={snap.indicatorId}
                  snap={snap}
                  onSelect={setSelected}
                />
              ))}
            </div>
          </section>

          {/* BCH Radar Pro */}
          <ProSection />

          <p className="pt-2 text-center text-xs text-muted-foreground">
            The entire radar is designed to be understood in ~10 seconds. Tap any
            factor for the full breakdown.
          </p>
        </div>
      )}

      <IndicatorDetailDialog
        snapshot={selected}
        open={selected !== null}
        onOpenChange={(v) => !v && setSelected(null)}
      />
    </AppLayout>
  );
};

function topDrivers(indicators: IndicatorSnapshot[]): IndicatorSnapshot[] {
  return [...indicators]
    .filter((i) => i.quality !== 'unavailable')
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution))
    .slice(0, 6);
}

function SectionHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <h2 className="text-lg font-bold tracking-tight">{title}</h2>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function ScoreCard({
  domain,
  title,
  subtitle,
  score,
  label,
  state,
  trend,
  series,
  to,
  colorClass,
}: {
  domain: 'liquidity' | 'bch';
  title: string;
  subtitle: string;
  score: number;
  label: string;
  state: import('@/lib/types').SignalState;
  trend: number;
  series: number[];
  to: string;
  colorClass: string;
}) {
  // Cap the sparkline window to the most recent ~90 points for readability.
  const spark = series.slice(-90);
  const hasHistory = spark.length >= 2;
  const sparkMin = hasHistory ? Math.min(...spark) : 0;
  const sparkMax = hasHistory ? Math.max(...spark) : 0;
  const sparkDelta = hasHistory ? spark[spark.length - 1] - spark[0] : 0;
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {title}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-4">
          <ScoreDial score={score} label={label} colorClass={colorClass} size={128} />
          <div className="flex-1 space-y-2">
            <SignalBadge state={state} />
            <p className="text-sm">
              <span className="text-muted-foreground">30-day change: </span>
              <span
                className={cn(
                  'font-mono font-semibold',
                  trend >= 0 ? 'text-primary' : 'text-red-400',
                )}
              >
                {signed(trend)}
              </span>
            </p>
            <Link
              to={to}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              Breakdown <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <div className="mt-3 border-t border-border/50 pt-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Score history
            </span>
            {hasHistory && (
              <span className="font-mono text-[10px] font-semibold">
                <span
                  className={cn(
                    sparkDelta >= 0 ? 'text-primary' : 'text-red-400',
                  )}
                >
                  {signed(Math.round(sparkDelta))}
                </span>
                <span className="ml-1 text-muted-foreground">
                  over {spark.length} readings
                </span>
              </span>
            )}
          </div>
          {hasHistory ? (
            <div className="mt-1.5 flex items-stretch gap-2">
              {/* Y-axis labels: fixed 0-100 score scale */}
              <div className="flex flex-col justify-between py-0.5 font-mono text-[9px] leading-none text-muted-foreground">
                <span>100</span>
                <span>50</span>
                <span>0</span>
              </div>
              <div className="relative flex-1">
                <Sparkline
                  data={spark}
                  colorClass={colorClass}
                  baseline={50}
                  className="h-12 w-full"
                />
              </div>
              {/* Current + range readout */}
              <div className="flex flex-col justify-center gap-1 border-l border-border/50 pl-2 text-right">
                <span className={cn('font-mono text-sm font-bold leading-none', colorClass)}>
                  {Math.round(spark[spark.length - 1])}
                </span>
                <span className="font-mono text-[9px] leading-none text-muted-foreground">
                  {Math.round(sparkMin)}–{Math.round(sparkMax)}
                </span>
              </div>
            </div>
          ) : (
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Building history — the trend line appears as readings accumulate
              (about one every 6 hours).
            </p>
          )}
        </div>
        <p className="mt-3 text-xs text-foreground/80">
          {interpretation(domain, score)}
        </p>
      </CardContent>
    </Card>
  );
}

function WatchTile({
  snap,
  onSelect,
}: {
  snap: IndicatorSnapshot;
  onSelect: (s: IndicatorSnapshot) => void;
}) {
  const def = INDICATORS_BY_ID[snap.indicatorId];
  return (
    <button
      onClick={() => onSelect(snap)}
      className="flex items-center justify-between rounded-xl border border-border/60 bg-card/60 p-3 text-left transition-colors hover:border-primary/50"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{def.short}</p>
        <p className="font-mono text-xs text-muted-foreground">
          {formatValue(def, snap.value)}
        </p>
      </div>
      <div className="ml-2 shrink-0 text-right">
        <p className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
          Signal
        </p>
        <span
          className={cn(
            'mt-0.5 inline-block rounded-md px-2 py-0.5 font-mono text-xs font-semibold',
            snap.contribution >= 0
              ? 'bg-primary/15 text-primary'
              : 'bg-red-500/15 text-red-400',
          )}
        >
          {signed(snap.contribution)}
        </span>
      </div>
    </button>
  );
}

function RadarBaseline({
  indicators,
  onSelect,
}: {
  indicators: IndicatorSnapshot[];
  onSelect: (s: IndicatorSnapshot) => void;
}) {
  return (
    <Card className="border-dashed">
      <CardContent className="space-y-4 p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Radar className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold">Building your baseline</p>
            <p className="mt-1 text-sm text-muted-foreground">
              BCH Radar is collecting historical observations. As enough history
              accumulates, meaningful changes will appear here automatically.
            </p>
          </div>
        </div>

        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Currently monitoring
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {indicators.map((snap) => {
              const def = INDICATORS_BY_ID[snap.indicatorId];
              return (
                <button
                  key={snap.indicatorId}
                  onClick={() => onSelect(snap)}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-card/60 p-2.5 text-left transition-colors hover:border-primary/50"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <DataStatus quality={snap.quality} showLabel={false} />
                    <span className="truncate text-sm font-medium">
                      {def.short}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    {snap.quality !== 'fresh' && (
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
                          snap.quality === 'stale'
                            ? 'bg-yellow-500/15 text-yellow-400'
                            : 'bg-red-500/15 text-red-400',
                        )}
                      >
                        {snap.quality === 'stale' ? 'Baseline' : 'N/A'}
                      </span>
                    )}
                    <span className="font-mono text-xs text-muted-foreground">
                      {formatValue(def, snap.value)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const PRO_HIGHLIGHTS = [
  'Advanced & custom alerts',
  'BCH rotation alerts',
  'Full historical data',
  'Advanced daily intelligence',
  'Push notifications',
  'Liquidity-cycle history',
];

function ProSection() {
  const { isPro } = useSubscription();
  if (isPro) return null;

  return (
    <Card className="relative overflow-hidden border-primary/30 bg-primary/[0.04]">
      <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
      <CardContent className="relative p-5">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <h2 className="text-lg font-bold tracking-tight">BCH Radar Pro</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Know when the environment changes.
        </p>

        <div className="mt-4 grid gap-x-4 gap-y-2 sm:grid-cols-2">
          {PRO_HIGHLIGHTS.map((f) => (
            <div key={f} className="flex items-center gap-2 text-sm">
              <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="text-foreground/90">{f}</span>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-col items-start justify-between gap-3 border-t border-border/50 pt-4 sm:flex-row sm:items-center">
          <div>
            <p className="text-2xl font-bold">
              ${PRICING.proMonthlyUsd}
              <span className="text-sm font-normal text-muted-foreground">
                {' '}
                / month
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              or ${PRICING.proYearlyUsd}/year · Bitcoin Cash only · non-custodial
            </p>
          </div>
          <Button asChild className="w-full gap-2 sm:w-auto">
            <Link to="/pricing">
              <Bell className="h-4 w-4" /> Pay with BCH
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-48" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
      <Skeleton className="h-32 w-full rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    </div>
  );
}

export default Index;
